import type { Destination as ApiDestination, InputTrack, InputTrackWarning, OutputStateReason, Profile, VideoCodec } from '../api'
import { currentLang, t, type Key } from '../i18n'

/*
 * Enhanced RTMP tracks in the cabinet (E1-UI, docs/ertmp.md §5–§8; design system InputTracks and
 * TrackSelection): what the encoder sends, which track each output gets and why it may get another
 * one, the voice track of captions. View rules only; texts come from the dictionary, numbers are
 * formatted by the language in force (04-typography «Numbers»).
 */

/** FourCC → the name people know. Unknown codes stay as they came. */
const CODEC: Record<string, string> = {
  avc1: 'H.264', avc3: 'H.264', hvc1: 'HEVC', hev1: 'HEVC', av01: 'AV1', vp09: 'VP9', vp08: 'VP8',
  mp4a: 'AAC', Opus: 'Opus', opus: 'Opus', fLaC: 'FLAC', 'ac-3': 'AC-3', 'ec-3': 'E-AC-3', '.mp3': 'MP3',
}
export const codecName = (fourcc: string) => CODEC[fourcc] ?? fourcc

const H264 = new Set(['avc1', 'avc3'])
/** Video codecs some output can send (H.264 everywhere; HEVC/AV1 to YouTube with E1h); others are ignored. */
const VIDEO_KNOWN = new Set(['avc1', 'avc3', 'hvc1', 'hev1', 'av01'])

/** The catalog's codec of a video FourCC (Profile.video_codecs), undefined for one no platform takes. */
export function videoCodecOf(fourcc: string): VideoCodec | undefined {
  return H264.has(fourcc) ? 'h264' : fourcc === 'hvc1' || fourcc === 'hev1' ? 'hevc' : fourcc === 'av01' ? 'av1' : undefined
}

/**
 * The video codecs a destination can send by Enhanced RTMP (E1h, docs/ertmp.md §14): H.264, plus the
 * profile's others (YouTube: HEVC, AV1) when enhanced_codecs is on and the protocol is RTMPS.
 */
export function sendCodecs(profile: Pick<Profile, 'video_codecs'> | undefined, protocol: string | undefined, enhanced: boolean): VideoCodec[] {
  const more = protocol === 'hls' ? [] : (profile?.video_codecs ?? []).filter((c) => c !== 'h264')
  return enhanced ? ['h264', ...more] : ['h264']
}

/** The codecs beyond H.264 the profile could take by this protocol (the checkbox is shown for these profiles only). */
export const extraCodecs = (profile: Pick<Profile, 'video_codecs'> | undefined) => (profile?.video_codecs ?? []).filter((c) => c !== 'h264')

/** What an output sends (E1c, §5.2; E1h): video in one of `codecs` (H.264 by default), audio as AAC. */
export function sendable(tr: Pick<InputTrack, 'kind' | 'codec'>, codecs: VideoCodec[] = ['h264']): boolean {
  if (tr.kind === 'audio') return tr.codec === 'mp4a'
  const c = videoCodecOf(tr.codec)
  return !!c && codecs.includes(c)
}

/** V0, A1: the compact name of a track (09-voice: Latin in every language). */
export const ixOf = (kind: InputTrack['kind'], index: number) => `${kind === 'video' ? 'V' : 'A'}${index}`

const LOCALE: Record<string, string> = { en: 'en-US', es: 'es-ES', ru: 'ru-RU' }
/** A number in the language in force: EN 1,250 · 13.5; ES 1.250 · 13,5; RU 1 250 · 13,5. */
export function num(v: number, digits = 0, fixed = false): string {
  return new Intl.NumberFormat(LOCALE[currentLang()] ?? 'en-US', {
    maximumFractionDigits: digits, minimumFractionDigits: fixed ? digits : 0, useGrouping: 'always',
  } as unknown as Intl.NumberFormatOptions).format(v)
}

/** A bitrate in the language in force: 5.8 Mbps from 1000 kbps up, else 160 kbps (no-break space before the unit). */
export function rate(kbps: number): string {
  return kbps >= 1000 ? `${num(kbps / 1000, 1, true)} ${t('unit.mbps')}` : `${num(Math.round(kbps))} ${t('unit.kbps')}`
}

/** A track is vertical when it is taller than wide (tag 9:16 whatever its exact ratio). */
export const vertical = (tr: Pick<InputTrack, 'width' | 'height'>) => !!tr.width && !!tr.height && tr.height > tr.width

/** Seconds since the track last carried media; undefined while it is live. */
export function endedFor(tr: InputTrack, now: number): number | undefined {
  if (tr.state === 'live') return undefined
  const at = tr.last_seen ? Date.parse(tr.last_seen) : NaN
  return Number.isFinite(at) ? Math.max(0, Math.round((now - at) / 1000)) : 0
}

/** First line of a row: what the eye checks — "1920×1080 · 30 fps", or the codec of an audio track. */
export function mainOf(tr: InputTrack): string {
  if (tr.kind === 'audio') return codecName(tr.codec)
  const size = tr.width && tr.height ? `${tr.width}×${tr.height}` : codecName(tr.codec)
  return tr.fps ? `${size} · ${num(tr.fps, tr.fps % 1 ? 2 : 0)} ${t('unit.fps')}` : size
}

export const byKind = (tracks: InputTrack[] | undefined, kind: InputTrack['kind']) => (tracks ?? []).filter((x) => x.kind === kind).sort((a, b) => a.index - b.index)

/** More than one video or more than one audio track: only then the cabinet shows tracks (InputTracks «Where»). */
export const multitrack = (tracks: InputTrack[] | undefined) => byKind(tracks, 'video').length > 1 || byKind(tracks, 'audio').length > 1

/** Plan limits of tracks (/api/me limits, E1H-FIN); undefined = none set. */
export function trackLimit(limits: Record<string, number | string | null> | undefined, kind: InputTrack['kind']): number | undefined {
  const v = limits?.[`${kind}_tracks_max`]
  return typeof v === 'number' && v >= 0 ? v : undefined
}

/**
 * Why a track is ignored, undefined when it is not: over the plan's count (the node keeps the plan's
 * number plus one spare track, docs/ertmp.md Q1a — what is above that is ignored) or a codec no output
 * can send.
 */
export function ignoredWhy(tr: InputTrack, tracks: InputTrack[], limits: Record<string, number | string | null> | undefined, plan?: string): string | undefined {
  if (tr.state !== 'live') return undefined // ended: says so itself
  if (tr.kind === 'video' ? !VIDEO_KNOWN.has(tr.codec) : tr.codec !== 'mp4a') return t('tracks.codec')
  const lim = trackLimit(limits, tr.kind)
  if (lim === undefined) return undefined
  const live = byKind(tracks, tr.kind).filter((x) => x.state === 'live')
  if (live.indexOf(tr) < lim + 1) return undefined
  return t(tr.kind === 'video' ? 'tracks.overVideo' : 'tracks.overAudio', { n: lim, plan: plan ?? t('tracks.yourPlan') })
}

/** What one output sends now: its tracks, or V0/A0 for the one the node replaced; null = nothing (failed). */
export type Sending = { video: number; audio: number } | null

/** How many outputs send a track now (InputTracks «3 out»): the working track counts, not the selected one. */
export function outputsOn(tr: InputTrack, sending: Sending[] | undefined): number {
  return (sending ?? []).filter((x) => x && (tr.kind === 'video' ? x.video : x.audio) === tr.index).length
}

/** What an enabled output sends now, from its tracks (outputTracks); a disabled one sends nothing. */
export function sendingOf(enabled: boolean, tr: OutputTracks | undefined): Sending {
  if (!enabled) return null
  return tr ? tr.sending : { video: 0, audio: 0 }
}

/* ── warnings of the input ─────────────────────────────────────────────── */

const WARN: Record<InputTrackWarning, Key> = {
  track_vertical_not_in_plan: 'tracks.warn.vertical',
  track_over_ingest_max: 'tracks.warn.ingestMax',
}

/** The API's plan warnings of the live tracks (InputView.track_warnings) as words. Warnings only: nothing is refused. */
export function inputWarnings(codes: InputTrackWarning[] | undefined, limits?: Record<string, number | string | null>): { code: InputTrackWarning; text: string }[] {
  return (codes ?? []).filter((c) => WARN[c]).map((c) => {
    const max = c === 'track_over_ingest_max' && typeof limits?.ingest_max === 'string' ? limits.ingest_max : undefined
    return { code: c, text: max ? t('tracks.warn.ingestMaxOf', { max }) : t(WARN[c]) }
  })
}

/* ── an output's track ─────────────────────────────────────────────────── */

export interface OutputTracks {
  video: number
  audio: number
  /** what it sends now (the working track instead of a missing or unsendable one); null = nothing */
  sending: Sending
  /** the row's words when it gets another track than the selected one */
  note?: { code: 'track_missing' | 'track_codec_unsupported'; text: string; tone: 'warn' | 'fail' }
}

/**
 * The tracks of an output and the words of its state_reason (Q3: the working track V0 with a warning;
 * Q9 with E1h: the same for a codec the output cannot send — before E1h such an output fails).
 * undefined: V0 / A0 and nothing to say (the row stays as it was).
 */
export function outputTracks(saved: Pick<ApiDestination, 'video_track' | 'audio_track' | 'enhanced_codecs'> | undefined, reason: OutputStateReason | undefined, state: string, platform: string, tracks?: InputTrack[]): OutputTracks | undefined {
  const video = saved?.video_track ?? 0
  const audio = saved?.audio_track ?? 0
  const liveOf = (kind: InputTrack['kind'], i: number) => byKind(tracks, kind).some((x) => x.index === i && x.state === 'live')
  let note: OutputTracks['note']
  let sending: Sending = { video, audio }
  if (reason === 'track_missing') {
    // the missing one: the selected video track if it is not live, else the audio one
    const kind: InputTrack['kind'] = video !== 0 && !liveOf('video', video) ? 'video' : audio !== 0 && !liveOf('audio', audio) ? 'audio' : video !== 0 ? 'video' : 'audio'
    note = { code: reason, tone: 'warn', text: t('row.trackMissing', { ix: ixOf(kind, kind === 'video' ? video : audio), to: ixOf(kind, 0) }) }
    sending = { ...sending, [kind]: 0 }
  } else if (reason === 'track_codec_unsupported') {
    const vt = byKind(tracks, 'video').find((x) => x.index === video)
    const at = byKind(tracks, 'audio').find((x) => x.index === audio)
    const bad = vt && !sendable(vt) ? vt : at && !sendable(at) ? at : vt
    const kind = bad?.kind ?? 'video'
    const ix = ixOf(kind, bad?.index ?? video)
    const codec = bad ? codecName(bad.codec) : '?'
    note = state === 'live'
      ? { code: reason, tone: 'warn', text: t('row.trackCodecSending', { ix, codec, to: ixOf(kind, 0) }) }
      : { code: reason, tone: 'fail', text: t('row.trackCodec', { ix, codec, platform }) }
    sending = state === 'live' ? { ...sending, [kind]: 0 } : null
  }
  if (!note && video === 0 && audio === 0) return undefined
  return { video, audio, sending, note }
}
